import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button, Card, CardBody, CardImg, Col, CustomInput, Row } from 'reactstrap';

import { fetcher } from 'api/client';
import { disableIntegration, setupIntegration } from 'api/actions/profile';
import { CONTROL_MODAL, useProfileViewDispatch } from 'context/profile-view';
import { toast } from 'react-toastify';
import bootbox from 'components/Modals/Bootbox';
import useQueryParams from 'hooks/useQueryParams';
import { useProfileAbility, useProfileState } from 'context/profile';
import { useHistory, useRouteMatch } from 'react-router-dom';

const slackClientId = process.env.REACT_APP_SLACK_CLIENT_ID;
const zoomClientId = 'hZaGJXPTNW3kGDqIgrjoA';

function useIntegration({ id, initialIsActive, codeKey = 'code', errorKey = 'error', onActivate = () => {}, onSetupIntegrationSuccess = () => {}}) {
    const [isActive, setIsActive] = useState(initialIsActive);
    const { t } = useTranslation();
    const history = useHistory();
    const queryParams = useQueryParams();
    const code = queryParams.get(codeKey);
    const integrationId = queryParams.get('integrationId');
    const error = queryParams.get(errorKey);

    const { mutate: mutateDisableIntegration } = useMutation(() => fetcher(disableIntegration(id)), {
        onSuccess: () => {
            setIsActive(false)
        }
    });

    const { mutate: mutateSetupIntegration, ...setupIntegrationState } = useMutation((payload) => fetcher(setupIntegration(id, payload)), {
        onSuccess: () => {
            onSetupIntegrationSuccess()
            setIsActive(true)
            history.replace('/profile?menu=integrations')
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    });

    useEffect(() => {
        const iid = integrationId || 'slack';
        if (code && iid === id) {
            mutateSetupIntegration({ code, redirect_uri: window.location.origin })
        } 
    }, [code, integrationId, id, mutateSetupIntegration]) 

    useEffect(() => {
        if (error) {
            bootbox.alert(error);
        }
    }, [error]) 
    
    const handleDisableIntegration = () => {
        const res = bootbox.confirm('¿Deseas desactivar la integración?')
        if (!res) return setIsActive(true)
        mutateDisableIntegration()
    }

    const handleChange = (e) => {
        const activate = e.target.checked;
        setIsActive(activate)

        if (activate) {
            onActivate()
        } else {
            handleDisableIntegration()
        }
    }

    return  {
        isActive,
        setIsActive,
        mutateSetupIntegration,
        mutateDisableIntegration,
        setupIntegrationState,
        onChange: handleChange,
        onDisableIntegration: handleDisableIntegration
    }
}

export function SlackIntegration({  }) {
    const { t } = useTranslation();
    const dispatch = useProfileViewDispatch();
    const { profile } = useProfileState();
    
    
    const { isActive, setupIntegrationState, onChange } = useIntegration({
        id: 'slack',
        initialIsActive: !!profile?.tenant?.slack,
        onActivate: () => {
            dispatch({ type: CONTROL_MODAL, open: 'slack' })
            window.open(`https://slack.com/oauth/v2/authorize?scope=incoming-webhook,commands&client_id=${slackClientId}&redirect_uri=${encodeURIComponent(`${window.location.origin}/profile?menu=integrations`)}`);
        },
        onSetupIntegrationSuccess: () => {
            toast.success('Slack se ha configurado correctamente')
        }
    })

    const handleKnowMore = () => {
        window.open('https://trato.io/slack-integration')
    }

    return (
        <IntegrationItem 
            label={t("integrations.slack_description" )}
            id="slack" 
            image="/images/integrations/slack.svg" 
            onChange={onChange} 
            onKnowMore={handleKnowMore} 
            isActive={isActive}
            isDisabled={setupIntegrationState.isLoading}
        />
    )
}

export function ZoomIntegration({  }) {
    const { t } = useTranslation();
    const dispatch = useProfileViewDispatch();
    const { profile } = useProfileState();
    
    console.log({profile})
    const { isActive, setupIntegrationState, onChange } = useIntegration({
        id: 'zoom',
        initialIsActive: !!profile?.zoom?.enabled,
        onActivate: () => {
            dispatch({ type: CONTROL_MODAL, open: 'slack' })
            window.open(`https://zoom.us/oauth/authorize?response_type=code&client_id=${zoomClientId}&redirect_uri=${encodeURIComponent(`${window.location.origin}/profile?menu=integrations&integrationId=zoom`)}`);
        }
    })

    // const handleKnowMore = () => {
    //     window.open('https://trato.io/slack-integration')
    // }

    return (
        <IntegrationItem 
            label={t("Integra a Zoom en tus documentos")}
            id="zoom" 
            image="/images/integrations/logo-Zoom.png" 
            onChange={onChange} 
            // onKnowMore={handleKnowMore} 
            isActive={isActive}
            isDisabled={setupIntegrationState.isLoading}
        />
    )
}

export default function Integrations() {
    const { t } = useTranslation();
    const profileAbility = useProfileAbility();
    
    return (
        <div>
            <h5>
                <i className="fas fa-plug"></i> {t('integrations.info')}
            </h5>
            <Row>
                {/* <Integration label="integrations.hubspot_description" id="hubspot" image="/images/integrations/hubspot.svg"></Integration> */}
                {/* <Integration label="integrations.kissflow_description" id="kissflow" image="/images/integrations/kissflow.svg"></Integration> */}
                
                <Col md="6" className="mb-3">
                    <SlackIntegration />
                </Col>
                <Col md="6" className="mb-3" hidden={profileAbility.cannot('VIEW_ZOOM_INTEGRATION')}>
                    <ZoomIntegration />
                </Col>
            </Row>
        </div>
    )
}

function IntegrationItem({ id, image, isDisabled, label, isActive, onChange, onKnowMore }) {
    const { t } = useTranslation();

    return (
        <Card className="rounded-lg text-center d-flex flex-column align-items-center" style={{minHeight: 295}}>
            <CardImg top src={image} className="" style={{ height: 120,  width: 220 }}>
            </CardImg>
            <CardBody>
                <CustomInput
                    onChange={onChange}
                    type="switch"
                    name={`${id}-switch`}
                    id={`${id}-switch`}
                    label={t('common.activate')}
                    className="mb-4"
                    checked={isActive}
                    disabled={isDisabled}
                />
                <h5>{label}</h5>
                <Button color="light" block hidden={!onKnowMore} onClick={onKnowMore}>{t('integrations.know_more')}</Button>
            </CardBody>
        </Card>
    )
}